import { EditableColumnType } from '@aon/aon-tws-common-ui/build/lib/components/EditableTable';
import * as React from 'react';
import ContentConstants from '../../contentConstants';
import { EditState, ITranslationEditItem } from '../../models';
import { TranslationHelper } from '../../utilities/TranslationHelper';
import { AddTranslationsButton } from './AddTranslationsButton';
import { TranslationEditorModal } from './TranslationEditorModal';

export interface IModalToggleButtonProps {
	loading: boolean;
	expectedCount: number;
	actualCount: number;
	checkThreshold: boolean;
	disabled: boolean;
	onClick: () => void;
}

export interface ITranslationEditorModuleProps {
	property: string;
	propertyDisplayName: string;
	supportedCultures: string[];
	defaultTranslation: ITranslationEditItem;
	loading: boolean;
	translations: ITranslationEditItem[];
	lastSavedTranslations: ITranslationEditItem[];
	enableHtmlEditor: boolean;
	inputType: EditableColumnType;
	maxLength?: number;
	modalToggleButton?: React.FunctionComponent<IModalToggleButtonProps>;
	disabled: boolean;
	saveInModal?: boolean;
	getTranslations: () => void;
	saveTranslations: (translations: ITranslationEditItem[]) => void;
	updateTranslations: (translations: ITranslationEditItem[]) => void;
	notifyUpdate: (item: ITranslationEditItem) => void;
	notifyTranslationEditorModalOpen: () => void;
}

export interface ITranslationEditorModuleState {
	isOpen: boolean;
}

export class TranslationEditorModule extends React.Component<ITranslationEditorModuleProps, ITranslationEditorModuleState> {
	static defaultProps = {
		saveInModal: true,
	};

	constructor(props: ITranslationEditorModuleProps) {
		super(props);
		this.state = { isOpen: false };

		this.handleSaveTranslations = this.handleSaveTranslations.bind(this);
		this.updateTranslations = this.updateTranslations.bind(this);
		this.toggleModal = this.toggleModal.bind(this);
	}

	public componentDidMount() {
		this.props.getTranslations();
	}

	public UNSAFE_componentWillUpdate(props: ITranslationEditorModuleProps) {
		if (!props.loading && props.defaultTranslation.modifiedOn !== this.props.defaultTranslation.modifiedOn) {
			this.props.getTranslations();
		}
	}

	public render() {
		const {
			defaultTranslation,
			enableHtmlEditor,
			loading,
			property,
			inputType,
			maxLength,
			propertyDisplayName,
			supportedCultures,
			modalToggleButton,
			disabled,
			saveInModal,
		} = this.props;
		const { isOpen } = this.state;

		const headerTitle = propertyDisplayName + ' Translations';

		const editableTranslations = TranslationHelper.getEditableTranslations([...this.props.translations], defaultTranslation.cultureCode);
		const lastSavedTranslations = TranslationHelper.getEditableTranslations(
			[...this.props.lastSavedTranslations],
			defaultTranslation.cultureCode,
		);
		const numberOfTranslations = TranslationHelper.getCountOfTranslations(editableTranslations);
		const numberOfSupportedCultures = TranslationHelper.getCountOfSupportedCultures(defaultTranslation.cultureCode, supportedCultures);

		const toggleButton = modalToggleButton ? (
			React.createElement(modalToggleButton, {
				loading: loading,
				onClick: this.toggleModal,
				expectedCount: numberOfSupportedCultures,
				actualCount: numberOfTranslations,
				checkThreshold: true,
				disabled: disabled,
			})
		) : (
			<AddTranslationsButton
				disabled={disabled}
				loading={loading}
				onClick={this.toggleModal}
				numberOfSupportedLanguages={numberOfSupportedCultures}
				numberOfTranslations={numberOfTranslations}
			/>
		);

		return (
			numberOfSupportedCultures > 0 && (
				<>
					{toggleButton}
					{isOpen && (
						<TranslationEditorModal
							inputType={inputType}
							maxLength={maxLength}
							isOpen={true}
							enableHtmlEditor={enableHtmlEditor}
							property={property}
							propertyDisplayName={propertyDisplayName}
							headerTitle={headerTitle}
							defaultTranslation={defaultTranslation}
							translations={editableTranslations}
							lastSavedTranslations={lastSavedTranslations}
							primaryButtonText={saveInModal ? ContentConstants.BUTTONS_SAVE_TRANSLATIONS : ContentConstants.BUTTONS_UPDATE}
							saveTranslations={saveInModal ? this.handleSaveTranslations : this.updateTranslations}
							onClose={this.toggleModal}
						/>
					)}
				</>
			)
		);
	}

	public handleSaveTranslations(translations: ITranslationEditItem[]) {
		// the default culture translation is not editable
		// but it should still be saved if changes were made outside of the editor.

		const nextTranslations = translations.concat(this.props.defaultTranslation);
		this.props.saveTranslations(nextTranslations);
		this.props.notifyUpdate({
			...this.props.defaultTranslation,
			modifiedOn: new Date(Date.now()),
			editState: EditState.None,
		});
		this.setState({ isOpen: false });
	}

	public updateTranslations(translations: ITranslationEditItem[]) {
		this.props.updateTranslations(translations.concat(this.props.defaultTranslation));

		this.props.notifyUpdate({
			...this.props.defaultTranslation,
			modifiedOn: new Date(Date.now()),
			editState: EditState.None,
		});

		this.setState({ isOpen: false });
	}

	public toggleModal() {
		if (!this.state.isOpen) this.props.notifyTranslationEditorModalOpen();
		this.setState({ isOpen: !this.state.isOpen });
	}
}
